/**
 * 의료기관 관련 서비스
 */

import { apiClient, ApiResponse } from "./api";
import type { Dataset } from "./consentService";

// 기관 정보 (공공기관 + 의료기관)
export interface Institution {
  provideInstitutionNo: string | null;
  provideInstitutionName: string | null;
  isConnected: boolean;
}

// API 응답 타입
export interface InstitutionsResponse {
  publicInstitutions: Institution[];
  hospitals: Institution[];
}

// UI용 기관 정보 (데이터셋 포함)
export interface InstitutionWithDatasets {
  provideInstitutionNo: string;
  provideInstitutionName: string;
  datasets: Dataset[];
  type: "public" | "hospital"; // 공공기관 or 의료기관
}

// 레거시 인터페이스 (호환성 유지)
export interface MedicalInstitution {
  institutionNo: string;
  institutionName: string;
  address?: string;
  hasAgreedDatasets: boolean;
}

export interface MedicalInstitutionDataset {
  institutionNo: string;
  institutionName: string;
  datasetCode: string;
  datasetName: string;
  essentialYN: string;
  agreementStatus: "AGREEMENT" | "NONE";
}

// 의료기관 공통 데이터셋 항목 (모든 병원에 동일하게 적용)
interface CommonDatasetItem {
  datasetCode: string;
  datasetName: string;
  essentialYN: string;
}

const commonMedicalDatasets: CommonDatasetItem[] = [
  {
    datasetCode: "DS_MED_001",
    datasetName: "진료기록",
    essentialYN: "Y",
  },
  {
    datasetCode: "DS_MED_002",
    datasetName: "검사결과",
    essentialYN: "Y",
  },
  {
    datasetCode: "DS_MED_003",
    datasetName: "처방전",
    essentialYN: "N",
  },
  {
    datasetCode: "DS_MED_004",
    datasetName: "영상검사",
    essentialYN: "N",
  },
  {
    datasetCode: "DS_MED_005",
    datasetName: "투약이력",
    essentialYN: "N",
  },
  {
    datasetCode: "DS_MED_006",
    datasetName: "건강검진",
    essentialYN: "N",
  },
];

// Mock 데이터: 의료기관 리스트
const mockMedicalInstitutions: MedicalInstitution[] = [
  {
    institutionNo: "H001",
    institutionName: "서울대학교병원",
    address: "서울특별시 종로구",
    hasAgreedDatasets: true,
  },
  {
    institutionNo: "H002",
    institutionName: "삼성서울병원",
    address: "서울특별시 강남구",
    hasAgreedDatasets: true,
  },
  {
    institutionNo: "H003",
    institutionName: "아산병원",
    address: "서울특별시 송파구",
    hasAgreedDatasets: false,
  },
  {
    institutionNo: "H004",
    institutionName: "세브란스병원",
    address: "서울특별시 서대문구",
    hasAgreedDatasets: false,
  },
  {
    institutionNo: "H005",
    institutionName: "서울아산병원",
    address: "서울특별시 송파구",
    hasAgreedDatasets: true,
  },
  {
    institutionNo: "H006",
    institutionName: "강남세브란스병원",
    address: "서울특별시 강남구",
    hasAgreedDatasets: false,
  },
];

// Mock 데이터: 병원별 데이터셋 동의 상태 (datasetCode: agreementStatus)
const mockInstitutionAgreements: Record<
  string,
  Record<string, "AGREEMENT" | "NONE">
> = {
  H001: {
    DS_MED_001: "AGREEMENT", // 진료기록
    DS_MED_002: "AGREEMENT", // 검사결과
    DS_MED_003: "NONE", // 처방전
    DS_MED_004: "NONE", // 영상검사
    DS_MED_005: "NONE", // 투약이력
    DS_MED_006: "NONE", // 건강검진
  },
  H002: {
    DS_MED_001: "AGREEMENT", // 진료기록
    DS_MED_002: "NONE", // 검사결과
    DS_MED_003: "NONE", // 처방전
    DS_MED_004: "AGREEMENT", // 영상검사
    DS_MED_005: "NONE", // 투약이력
    DS_MED_006: "NONE", // 건강검진
  },
  H003: {
    DS_MED_001: "NONE", // 진료기록
    DS_MED_002: "NONE", // 검사결과
    DS_MED_003: "NONE", // 처방전
    DS_MED_004: "NONE", // 영상검사
    DS_MED_005: "NONE", // 투약이력
    DS_MED_006: "NONE", // 건강검진
  },
  H004: {
    DS_MED_001: "NONE", // 진료기록
    DS_MED_002: "NONE", // 검사결과
    DS_MED_003: "NONE", // 처방전
    DS_MED_004: "NONE", // 영상검사
    DS_MED_005: "NONE", // 투약이력
    DS_MED_006: "NONE", // 건강검진
  },
  H005: {
    DS_MED_001: "AGREEMENT", // 진료기록
    DS_MED_002: "AGREEMENT", // 검사결과
    DS_MED_003: "AGREEMENT", // 처방전
    DS_MED_004: "NONE", // 영상검사
    DS_MED_005: "AGREEMENT", // 투약이력
    DS_MED_006: "NONE", // 건강검진
  },
  H006: {
    DS_MED_001: "NONE", // 진료기록
    DS_MED_002: "NONE", // 검사결과
    DS_MED_003: "NONE", // 처방전
    DS_MED_004: "NONE", // 영상검사
    DS_MED_005: "NONE", // 투약이력
    DS_MED_006: "NONE", // 건강검진
  },
};

/**
 * 기관 목록 조회 (공공기관 + 의료기관)
 */
export const getInstitutions = async (): Promise<InstitutionsResponse> => {
  try {
    const response: ApiResponse<InstitutionsResponse> = await apiClient.get(
      `/datasets/institutions`
    );
    return response.data;
  } catch (error) {
    console.error("기관 목록 조회 실패:", error);
    throw error;
  }
};

/**
 * 데이터셋 목록 조회
 */
export const getDatasetsList = async (): Promise<Dataset[]> => {
  try {
    const response: ApiResponse<Dataset[]> = await apiClient.get(
      `/datasets/list`
    );
    return response.data;
  } catch (error) {
    console.error("데이터셋 목록 조회 실패:", error);
    throw error;
  }
};

/**
 * 기관과 데이터셋을 매핑하여 UI용 데이터 생성
 */
export const getInstitutionsWithDatasets = async (): Promise<{
  publicInstitutions: InstitutionWithDatasets[];
  hospitals: InstitutionWithDatasets[];
}> => {
  try {
    const [institutions, datasets] = await Promise.all([
      getInstitutions(),
      getDatasetsList(),
    ]);

    // provisionDivisionCode가 "10"인 공통 데이터셋 추출
    const commonDatasets = datasets.filter(
      (dataset) => dataset.provisionDivisionCode === "10"
    );

    // 데이터셋을 provideInstitutionNo별로 그룹핑
    const datasetsByInstitution = new Map<string, Dataset[]>();
    datasets.forEach((dataset) => {
      const institutionNo = dataset.provideInstitutionNo || "public";
      if (!datasetsByInstitution.has(institutionNo)) {
        datasetsByInstitution.set(institutionNo, []);
      }
      datasetsByInstitution.get(institutionNo)!.push(dataset);
    });

    // 공공기관 매핑
    const publicInstitutions: InstitutionWithDatasets[] =
      institutions.publicInstitutions
        .filter((inst) => inst.provideInstitutionNo)
        .map((inst) => ({
          provideInstitutionNo: inst.provideInstitutionNo!,
          provideInstitutionName: inst.provideInstitutionName || "",
          isConnected: inst.isConnected,
          datasets: datasetsByInstitution.get(inst.provideInstitutionNo!) || [],
          type: "public" as const,
        }));

    // 의료기관 매핑 (provisionDivisionCode "10"인 공통 데이터셋 포함)
    const hospitals: InstitutionWithDatasets[] = institutions.hospitals
      .filter((inst) => inst.provideInstitutionNo)
      .map((inst) => {
        const institutionDatasets =
          datasetsByInstitution.get(inst.provideInstitutionNo!) || [];
        // 해당 기관의 데이터셋 + 공통 데이터셋(provisionDivisionCode "10")
        const allDatasets = [...institutionDatasets, ...commonDatasets];

        return {
          provideInstitutionNo: inst.provideInstitutionNo!,
          provideInstitutionName: inst.provideInstitutionName || "",
          isConnected: inst.isConnected,
          datasets: allDatasets,
          type: "hospital" as const,
        };
      });

    return {
      publicInstitutions,
      hospitals,
    };
  } catch (error) {
    console.error("기관 및 데이터셋 매핑 실패:", error);
    throw error;
  }
};

/**
 * 의료기관 리스트 조회 (레거시 - 호환성 유지)
 * @deprecated getInstitutionsWithDatasets 사용 권장
 * @param filterType 'agreed' | 'not-agreed' | 'all'
 */
export const getMedicalInstitutions = async (
  filterType: "agreed" | "not-agreed" | "all" = "all"
): Promise<MedicalInstitution[]> => {
  try {
    const response: ApiResponse<MedicalInstitution[]> = await apiClient.get(
      `/medical-institutions?filterType=${filterType}`
    );
    return response.data;
  } catch (error) {
    console.error("의료기관 목록 조회 실패:", error);
    // 개발 환경에서는 Mock 데이터 반환
    if (import.meta.env.MODE === "development") {
      console.warn("개발 모드: Mock 데이터를 사용합니다.");
      await new Promise((resolve) => setTimeout(resolve, 300));
      let filtered = mockMedicalInstitutions;
      if (filterType === "agreed") {
        filtered = mockMedicalInstitutions.filter(
          (inst) => inst.hasAgreedDatasets
        );
      } else if (filterType === "not-agreed") {
        filtered = mockMedicalInstitutions.filter(
          (inst) => !inst.hasAgreedDatasets
        );
      }
      return filtered;
    }
    throw error;
  }
};

/**
 * 특정 의료기관의 데이터셋 조회
 * @param institutionNo 의료기관 번호
 */
export const getMedicalInstitutionDatasets = async (
  institutionNo: string
): Promise<MedicalInstitutionDataset[]> => {
  // Mock API 지연 시뮬레이션
  await new Promise((resolve) => setTimeout(resolve, 300));

  const institution = mockMedicalInstitutions.find(
    (inst) => inst.institutionNo === institutionNo
  );

  if (!institution) {
    return [];
  }

  // 공통 데이터셋 항목에 병원별 동의 상태를 조합
  const agreements = mockInstitutionAgreements[institutionNo] || {};

  const datasets: MedicalInstitutionDataset[] = commonMedicalDatasets.map(
    (commonDataset) => ({
      institutionNo: institution.institutionNo,
      institutionName: institution.institutionName,
      datasetCode: commonDataset.datasetCode,
      datasetName: commonDataset.datasetName,
      essentialYN: commonDataset.essentialYN,
      agreementStatus: agreements[commonDataset.datasetCode] || "NONE",
    })
  );

  return datasets;
};

/**
 * 의료기관 데이터셋 동의 업데이트
 * @param institutionNo 의료기관 번호
 * @param datasets 업데이트할 데이터셋 리스트
 */
export const updateMedicalInstitutionDatasets = async (
  institutionNo: string,
  datasets: MedicalInstitutionDataset[]
): Promise<{ success: boolean; message: string }> => {
  // Mock API 지연 시뮬레이션
  await new Promise((resolve) => setTimeout(resolve, 500));

  // Mock 데이터 업데이트: 동의 상태만 저장
  const agreements: Record<string, "AGREEMENT" | "NONE"> = {};
  datasets.forEach((dataset) => {
    agreements[dataset.datasetCode] = dataset.agreementStatus;
  });
  mockInstitutionAgreements[institutionNo] = agreements;

  // 병원의 hasAgreedDatasets 상태 업데이트
  const hasAgreed = datasets.some((ds) => ds.agreementStatus === "AGREEMENT");
  const institution = mockMedicalInstitutions.find(
    (inst) => inst.institutionNo === institutionNo
  );
  if (institution) {
    institution.hasAgreedDatasets = hasAgreed;
  }

  return {
    success: true,
    message: "데이터셋 동의가 성공적으로 업데이트되었습니다.",
  };
};
